import {
  collection,
  doc,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  FirestoreError,
  getDoc,
  onSnapshot,
} from 'firebase/firestore';
import { firestore } from 'services';

/**
 * Single fetch of document without subscribing to changes.
 *
 * @param path POSIX style document path.
 */
export const getDocument = <T = DocumentData>(path: string) => {
  const documentReference = getDocumentReference<T>(path);
  return getDoc<T, DocumentData>(documentReference);
};

// Helper types for subscribeToDocument.
type OnSnapshot<T = DocumentData> = (snapshot: DocumentSnapshot<T>) => void;
type OnError = (error: FirestoreError) => void;

/**
 * Get document data and subscribe to changes.
 *
 * @example
 * // You can add your own expected response type instead of DocumentData and
 * // it will be automatically *asserted*.
 * const [data, setData] = useState<DocumentData | null>(null);
 * const [error, setError] = useState<FirestoreError | null>(null);
 *
 * subscribeToDocument('usersWeb/${props.artistId}', (snapshot) => {
 *    setData(snapshot.data())
 * }, (error) => {
 *    setError(error);
 * });
 *
 * @param path POSIX style document path.
 * @param onSnapshotHandler Updated data handler function.
 * @param onErrorHandler Error handler.
 */
export const subscribeToDocument = <T = DocumentData>(
  path: string,
  onSnapshotHandler: OnSnapshot<T>,
  onErrorHandler: OnError,
) => {
  const documentReference = getDocumentReference<T>(path);
  return onSnapshot(
    documentReference,
    (snapshot: DocumentSnapshot<T>) => onSnapshotHandler(snapshot),
    (error) => onErrorHandler(error),
  );
};

/**
 * Convenience function for getting document reference from given path,
 * similar to how it works on admin sdk.
 *
 * @param path POSIX style document path.
 */
export const getDocumentReference = <T = DocumentData>(path: string) => {
  const documentId = extractDocumentIdFromPath(path);
  const collectionPath = path.replace(`/${documentId}`, '');
  const collectionReference = collection(firestore, collectionPath);

  return doc(collectionReference, documentId) as DocumentReference<T>;
};

/**
 * Extract id of a firestore document by selecting last string chain after forward slash
 *
 * @param path POSIX style document path.
 */
function extractDocumentIdFromPath(path: string) {
  // Expected output example for valid path: Array [ "/L2qxwgCgVWJr01DL2eak", "L2qxwgCgVWJr01DL2eak" ]
  const searchResults = path.match(/\/(\w+)$/);

  if (!searchResults) throw new Error('error/malformed-path');

  return searchResults[1];
}
